/* Copyright CloudLinux Zug GmbH 2010-2017 All Rights Reserved
 *
 * Licensed under CLOUD LINUX LICENSE AGREEMENT
 * http://cloudlinux.com/docs/LICENSE.TXT
 */

import { Component, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { BaseComponent } from '../../app/utils/base_component';
import { AppState } from '../../app/app.service';
import { SelectorRepo } from '../../app/repository/selector_repo';
import { Column } from '../../app/utils/column';
import { ApplicationRow } from '../../app/nodejs/application/application.row';
import { GroupedGrid } from '../../app/utils/grouped_grid';
import { Router } from '@angular/router';
import { ApplicationCommand, AppStatus, NodeJSInformation } from '../../app/nodejs/model';
import { NotificationsService } from '../../app/services/notifiaction_service';
import { Status } from '../../app/model/status';
import { Message } from '../../app/model/message';
import { Tool } from '../../app/model/tool';
import { AppService } from '../app.service';
import { ModalDialog } from '../../app/utils/ui-elements/modal-dialog/modal-dialog';
import { TranslateClass } from '../../app/utils/pipes/translate';
import { WindowRef } from '../../app/services/window.service';
import { LoaderSelector } from '../../app/utils/loader';

@Component({
    template: require('./applist.component.html'),
    styles: [
        require('./applist.component.less'),
    ],
    providers: [SelectorRepo]
})
export class AppListComponent extends BaseComponent implements AfterViewInit {
    loaderSelector = new LoaderSelector('.applist-component grid', true);
    userName: string;
    columns: Column[] = [];
    rows: ApplicationRow[] = [];
    rowsOnPage: number = 5;
    data: NodeJSInformation = null;

    headerConfirmationWindow: Message;

    @ViewChild(GroupedGrid) grid: GroupedGrid;
    @ViewChild('confirmationWindow') confirmationWindow: ModalDialog;

    constructor(
        public appState: AppState,
        private appService: AppService,
        private selectorRepo: SelectorRepo,
        private router: Router,
        private winRef: WindowRef,
        private notifictions: NotificationsService,
        private changeDetector: ChangeDetectorRef
    ) {
        super();
        this.userName = this.appState.get('userName');
    }

    ngAfterViewInit() {
        this.grid.noFoundText = 'INFO.noApplicationsFound';
        this.changeDetector.detectChanges();
        this.load();
    }

    public async load() {
        try {
            this.loading = true;
            this.data = await this.appService.data(this.userName);
            this.rows = this.prepareRows();
            this.columns = this.getColumns();
        } finally {
            this.loading = false;
        }
    }

    public prepareRows(): ApplicationRow[] {
        let rows: ApplicationRow[] = [];
        let versions = this.data.versions;
        Object.keys(versions).forEach((versionNumber) => {
            if (versions[versionNumber].users.hasOwnProperty(this.userName)) {
                let user = versions[versionNumber].users[this.userName];
                let apps = user.applications;
                Object.keys(apps).forEach((appName) => {
                    let row = new ApplicationRow();
                    this.loadRowData(row, versionNumber, appName);
                    rows.push(row);
                });
            }
        });
        return rows;
    }

    public loadRowData(row: ApplicationRow, versionNumber: string, appName: string) {
        let user = this.data.versions[versionNumber].users[this.userName];
        let app = user.applications[appName];
        row.appUri = app.domain + '/' + app.app_uri;
        row.rootDir = user.homedir + '/' + appName;
        row.mode = app.app_mode;
        row.runningStatus = new Status(
            app.app_status, new TranslateClass().transform([
                `${app.app_status} (v%(version)s)`,
                {version: versionNumber}
            ])
        );
        row.status = app.app_status;
        row.appName = appName;
        row.version = versionNumber;
        row.tools = [
            app.isStarted
                ? new Tool(
                'stop', new Message('Stop the application'), 'stop')
                : null,
            !app.isStarted
                ? new Tool('start', new Message('Start the application'), 'start')
                : null,
            app.isStarted
                ? new Tool('restart', new Message('Restart the application'), 'restart')
                : null,
            new Tool('edit', new Message('Edit the application'), 'edit'),
            new Tool('delete', new Message('Delete the application'), 'delete'),
        ].filter(tool => !!tool);
    }

    public getColumns(): Column[] {
        return [
            new Column({name: 'appUri', descr: 'App URI', convertToFormat: 'string'})
                .setAction(async (event, row) => {
                    this.winRef.openExternalHttpTab(row.appUri);
                }),
            new Column({name: 'rootDir', descr: 'App Root Directory', convertToFormat: 'string'}),
            new Column({name: 'mode', descr: 'Mode', convertToFormat: 'string'}),
            new Column({
                name: 'status', descr: 'Status', convertToFormat: 'string'
            }).makeRunningStatus(),
            new Column({
                name: 'actions', descr: 'Actions', convertToFormat: null
            }).sortableDisable(),
        ];
    }

    public actionClicked(event) {
        if (event.hasOwnProperty('action')) {
            switch (event.action) {
                case 'edit':
                    this.editAction(event);
                    break;
                case 'start':
                    this.startAction(event);
                    break;
                case 'stop':
                    this.stopAction(event);
                    break;
                case 'restart':
                    this.restartAction(event);
                    break;
                case 'delete':
                    this.deleteAction(event);
                    break;
                default:
                    this.notifictions.error('Error', 'ERROR.undefined_action');
                    return;
            }
        }
    }

    public editAction(event) {
        this.router.navigateByUrl('/applications/' + event.row.appNameEncoded);
    }

    public async startAction(event) {

        await this.changeStatus('start', event, 'started');
    }

    public async stopAction(event) {
        await this.changeStatus('stop', event, 'stopped');
    }

    public async restartAction(event) {
        await this.changeStatus('restart', event, 'started');
    }

    public async deleteAction(event) {
        try {
            this.headerConfirmationWindow = new Message(
                'confirmationMessage.removeApplication',
                {appName: event.row['appName']}
            );
            await this.confirmationWindow.open();
            await this.changeStatus('destroy', event);
        } catch (e) {}
    }

    public async changeStatus(command: ApplicationCommand, event, status?: AppStatus) {
        if (!event.hasOwnProperty('row')) {
            this.notifictions.error('Error', 'ERROR.wrong_received_data');
            return;
        }
        let row: ApplicationRow = event.row;
        try {
            row.loadingStage = true;
            await this.selectorRepo.sendApplicationCommand(command, row.appName);
            if (status) {
                await this.appService.changeApplicationStatus(
                    status, row.version, this.userName, row.appName
                );
                this.loadRowData(row, row.version, row.appName);
            } else {
                await this.appService.deleteApplication(
                    row.version, this.userName, row.appName
                );
                this.rows = this.prepareRows();
            }
        } catch (e) {
            this.notifictions.error('Error', 'ERROR.wrong_received_data');
        } finally {
            row.loadingStage = false;
        }
    }

}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./node_modules/angular-router-loader/src?loader=system&genDir=compiled&aot=false!./src/selector/applist-component/applist.component.ts